/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';

export interface SpreaderStatusBadgeProps {
  label: string;
}

const useStyles = makeStyles(() => ({
  badge : {
    boxSizing       : 'border-box',
    display         : 'inline-flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    padding         : '4px',
    borderRadius    : '4px',
    backgroundColor : dlsColor('paleBlue'),
    boxShadow       : '0px 1px 2px rgba(0, 0, 0, 0.12)'
  },
  label : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '12px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textAlign     : 'center',
    color         : dlsColor('grey2')
  }
}));

/**
 * SpreaderStatusBadge renders the current theme's pale-blue Spreader status
 * pill (e.g. "In Standby"), per the "Tag/Availability/Default" Figma frame
 * for Spreader Type 1. `Chip` (`@ion/dls/src/components/chip/Chip`) was
 * checked first and doesn't fit - it requires a `disabled` prop and carries
 * delete/select semantics meant for filter chips, and its own current-theme
 * typography/radius (Roboto, 8px radius) don't match this spec's Inter
 * 600 12px label, 4px radius, and drop-shadow. `AssetSummaryCardStatus`
 * (`@ion/dls/src/components/assetcard/current/AssetSummaryCardStatus`)
 * establishes the same precedent - it renders its own state tag rather than
 * force-fitting `Chip` - so this component follows suit with a small,
 * dedicated pill.
 *
 * ```typescript
 *
 *   import {SpreaderStatusBadge} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderStatusBadge';
 *   ...
 *
 *   <SpreaderStatusBadge label='In Standby' />
 *
 * ```
 *
 */
export const SpreaderStatusBadge = ({label}: SpreaderStatusBadgeProps) => {
  const classes = useStyles();
  return (
    <span className={classes.badge}>
      <span className={classes.label}>
        {label}
      </span>
    </span>
  );
};

export default memo(SpreaderStatusBadge);
